import React, { useState } from "react";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router-dom";
import {
  IconButton,
  Box,
  Menu,
  MenuItem,
  Typography,
  Avatar,
  Divider,
  ListItemIcon,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LogoutIcon from "@mui/icons-material/Logout";
import LaunchIcon from "@mui/icons-material/Launch";
import SettingsIcon from "@mui/icons-material/Settings";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { useIsBhoonidhiUser } from "features/explore/bhoonidhiAccess";
import { useIsSuperAdmin } from "features/platform/superAdminAccess";
import BhoonidhiSettingsDialog from "features/explore/dialogs/BhoonidhiSettingsDialog";

const ProfileMenu = ({ user, iconButtonSx }) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const isBhoonidhi = useIsBhoonidhiUser();
  const isSuperAdmin = useIsSuperAdmin();
  const [anchorEl, setAnchorEl] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      // signoutRedirect() clears the local OIDC user, then bounces the browser
      // to Zitadel's end_session_endpoint with id_token_hint, which kills the
      // server-side session before redirecting to post_logout_redirect_uri.
      await auth.signoutRedirect();
    } catch (err) {
      // If Zitadel's end-session call fails, at least clear local state so
      // the user isn't stuck "logged in" client-side.
      await auth.removeUser();
      window.location.replace("/login");
    }
  };

  return (
    <>
      <IconButton
        sx={iconButtonSx}
        onClick={handleClick}
        aria-controls={open ? "profile-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
      >
        <PersonOutlineIcon sx={{ fontSize: 25 }} />
      </IconButton>

      <Menu
        id="profile-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 3,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.15))",
            mt: 1.5,
            width: 220,
            borderRadius: "8px",
            zIndex: 9999,
            "&::before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: -10,
              right: 14,
              width: 0,
              height: 0,
              borderLeft: "10px solid transparent",
              borderRight: "10px solid transparent",
              borderBottom: "10px solid white",
              zIndex: 0,
            },
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        sx={{ zIndex: 9999 }}
      >
        {/* User Profile Section */}
        <Box sx={{ p: 2, display: "flex", alignItems: "center" }}>
          <Avatar
            sx={{
              bgcolor: "#999",
              color: "white",
              width: 40,
              height: 40,
              fontSize: "16px",
              fontWeight: "bold",
            }}
          >
            {user.initials}
          </Avatar>
          <Box sx={{ ml: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
              {user.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user.role}
            </Typography>
          </Box>
        </Box>

        <Divider />

        {/* Menu Options */}
        {isSuperAdmin && (
          <MenuItem onClick={() => { handleClose(); navigate("/platform"); }}>
            <ListItemIcon>
              <AdminPanelSettingsIcon fontSize="small" />
            </ListItemIcon>
            Platform Options
          </MenuItem>
        )}

        {isBhoonidhi && (
          <MenuItem onClick={() => setSettingsOpen(true)}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            Bhoonidhi Settings
          </MenuItem>
        )}

        <MenuItem
          component="a"
          href="https://www.kaleideo.co"
          target="_blank"
        >
          <ListItemIcon>
            <LaunchIcon fontSize="small" />
          </ListItemIcon>
          Visit Website
        </MenuItem>

        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      <BhoonidhiSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
    </>
  );
};

export default ProfileMenu;
